import {
  Alert,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  buttonVariants,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@simplicity/ui";
import { Trash2, X } from "lucide-react";
import { useContext, useState } from "react";
import { TransformCrossRefContext } from "../TransformCrossRefContext";
import { DnAEndpointField, DnAEndpointNotice } from "./DnAEndpointField";
import { FieldsEditor } from "./FieldsEditor";
import { FieldsTable } from "./FieldsTable";
import type { TableConfig } from "./types";

function getDependentTransformNames(
  tableName: string,
  prefix: string,
  transforms: { name: string; dependencies: { sourceTable: string }[] }[],
): string[] {
  return transforms
    .filter((t) =>
      t.dependencies.some(
        (d) =>
          d.sourceTable === `${prefix}__${tableName}__latest` ||
          d.sourceTable === `${prefix}__${tableName}__snapshots`,
      ),
    )
    .map((t) => t.name);
}

interface ApiTableRowProps {
  table: TableConfig;
  index: number;
  onChange: (table: TableConfig) => void;
  onRemove: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  connectorType: string;
}

function TableNameInput(props: {
  table: TableConfig;
  index: number;
  update: (patch: Partial<TableConfig>) => void;
  lowercase: boolean;
}) {
  const { table, index, update, lowercase } = props;
  return (
    <div className="flex-1">
      <Label htmlFor={`table-name-${index}`} className="form-label">
        Table Name
      </Label>
      <Input
        id={`table-name-${index}`}
        type="text"
        value={table.name}
        onChange={(e) =>
          update({ name: lowercase ? e.target.value.toLowerCase() : e.target.value })
        }
        placeholder="e.g. students"
        className="font-mono"
      />
    </div>
  );
}

function PrimaryKeyInput(props: {
  table: TableConfig;
  index: number;
  update: (patch: Partial<TableConfig>) => void;
}) {
  const { table, index, update } = props;
  const isCompositeKey = Array.isArray(table.primaryKey);

  return (
    <div className="flex-1">
      <Label htmlFor={`table-pk-${index}`} className="form-label">
        Primary Key
      </Label>
      {isCompositeKey ? (
        <FieldsEditor
          fields={table.primaryKey as string[]}
          onChange={(pk) => update({ primaryKey: pk })}
          label=""
          placeholder="Add key field\u2026"
        />
      ) : (
        <Input
          id={`table-pk-${index}`}
          type="text"
          value={table.primaryKey}
          onChange={(e) => update({ primaryKey: e.target.value })}
          placeholder="e.g. id"
          className="font-mono"
        />
      )}
    </div>
  );
}

function DeansListTableFields(props: {
  table: TableConfig;
  index: number;
  update: (patch: Partial<TableConfig>) => void;
}) {
  const { table, index, update } = props;
  return (
    <div className="flex gap-4">
      <div className="flex-1">
        <Label htmlFor={`table-api-${index}`} className="form-label">
          API Type
        </Label>
        <Select value={table.api ?? "graphql"} onValueChange={(value) => update({ api: value })}>
          <SelectTrigger id={`table-api-${index}`} className="font-mono">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="graphql">graphql</SelectItem>
            <SelectItem value="standard-rest">standard-rest</SelectItem>
            <SelectItem value="fast-rest">fast-rest</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex-1">
        <Label htmlFor={`table-lookback-${index}`} className="form-label">
          Lookback Days
        </Label>
        <Input
          id={`table-lookback-${index}`}
          type="number"
          value={(table.lookbackDays as number | undefined) ?? ""}
          onChange={(e) =>
            update({ lookbackDays: e.target.value ? Number(e.target.value) : undefined })
          }
          placeholder="Connector default"
          className="font-mono"
        />
      </div>
    </div>
  );
}

function DefaultFiltersEditor({
  filters,
  onChange,
}: {
  filters: Record<string, string | number>;
  onChange: (filters: Record<string, string | number>) => void;
}) {
  const entries = Object.entries(filters);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");

  function addFilter() {
    const key = newKey.trim();
    if (!key) return;
    onChange({ ...filters, [key]: newValue });
    setNewKey("");
    setNewValue("");
  }

  function removeFilter(key: string) {
    const next = { ...filters };
    delete next[key];
    onChange(next);
  }

  return (
    <div>
      <span className="form-label">Default Filters</span>
      {entries.length > 0 && (
        <div className="mb-2 space-y-1.5">
          {entries.map(([key, value]) => (
            <div key={key} className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {key}
              </Badge>
              <span className="text-xs text-muted-foreground">=</span>
              <span className="text-xs">{String(value)}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto p-0.5"
                onClick={() => removeFilter(key)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Input
          type="text"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          placeholder="key"
          className="h-7 w-28 font-mono text-xs"
        />
        <span className="text-xs text-muted-foreground">=</span>
        <Input
          type="text"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder="value"
          className="h-7 w-28 font-mono text-xs"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addFilter();
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={addFilter}
        >
          Add
        </Button>
      </div>
    </div>
  );
}

function PSEnrollmentTableFields(props: {
  table: TableConfig;
  update: (patch: Partial<TableConfig>) => void;
}) {
  const { table, update } = props;
  // BACKCOMPAT SIM-1378 \u2014 remove after data migration confirmed clean
  const legacyParams = (table as { params?: unknown }).params;
  const declaredParams =
    table.requiredParams ?? (Array.isArray(legacyParams) ? (legacyParams as string[]) : []);
  return (
    <>
      <FieldsEditor
        fields={declaredParams}
        onChange={(requiredParams) =>
          update({ requiredParams: requiredParams.length > 0 ? requiredParams : undefined })
        }
        label="Route Params"
        placeholder="Add param key\u2026"
      />
      <DefaultFiltersEditor
        filters={table.defaultFilters ?? {}}
        onChange={(filters) =>
          update({ defaultFilters: Object.keys(filters).length > 0 ? filters : undefined })
        }
      />
    </>
  );
}

function RemoveTableButton({
  tableName,
  onRemove,
  dependentTransformNames,
}: {
  tableName: string;
  onRemove: () => void;
  dependentTransformNames: string[];
}) {
  const hasWarning = dependentTransformNames.length > 0;
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="compact"
          aria-label={`Remove table ${tableName || "untitled"}`}
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="h-5 w-5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove table</AlertDialogTitle>
          <AlertDialogDescription>
            Remove &quot;{tableName || "untitled"}&quot; from this connector? This cannot be undone.
          </AlertDialogDescription>
          {hasWarning && (
            <Alert variant="warning" className="mt-2">
              <p className="font-medium">Warning: This table is referenced by transforms</p>
              <ul className="mt-1 list-inside list-disc text-xs">
                {dependentTransformNames.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-muted-foreground">
                Removing this table may break these transforms.
              </p>
            </Alert>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onRemove}
            className={buttonVariants({ variant: "destructive" })}
          >
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function ApiTableRow({
  table,
  index,
  onChange,
  onRemove,
  isExpanded,
  onToggleExpand: _onToggleExpand,
  connectorType,
}: ApiTableRowProps) {
  const crossRef = useContext(TransformCrossRefContext);
  const dependentTransformNames =
    crossRef && table.name
      ? getDependentTransformNames(table.name, crossRef.connectorPrefix, crossRef.transforms)
      : [];

  function update(patch: Partial<TableConfig>) {
    onChange({ ...table, ...patch });
  }

  if (!isExpanded) return null;

  // DnA routes are case-sensitive while bronze names are lowercase, so the endpoint is its own field.
  const isDna = connectorType === "dna";

  return (
    <div className="space-y-4 px-4 py-4">
      <div className="flex items-end gap-4">
        {isDna && <DnAEndpointField table={table} index={index} update={update} />}
        <TableNameInput table={table} index={index} update={update} lowercase={isDna} />
        <PrimaryKeyInput table={table} index={index} update={update} />
        <RemoveTableButton
          tableName={table.name}
          onRemove={onRemove}
          dependentTransformNames={dependentTransformNames}
        />
      </div>

      {isDna && <DnAEndpointNotice table={table} update={update} />}

      <FieldsTable
        fields={table.fields ?? []}
        aliases={table.fieldAliases ?? {}}
        onFieldsChange={(fields) => update({ fields })}
        onAliasesChange={(aliases) =>
          update({
            fieldAliases: Object.keys(aliases).length > 0 ? aliases : undefined,
          })
        }
      />

      {connectorType === "deanslist" && (
        <DeansListTableFields table={table} index={index} update={update} />
      )}

      {connectorType === "powerschool-enrollment" && (
        <PSEnrollmentTableFields table={table} update={update} />
      )}
    </div>
  );
}
